/**
 * SPEC.md §3, §8 — the topbar's diagram theme menu, disabled since M0. M6.6
 * (issue #33) turns it into a real dropdown: SPEC.md §8's exact 5 Mermaid
 * diagram themes. Mirrors `NewMenu.tsx`'s shape, built on the same
 * `useMenu` hook.
 *
 * "diagramTheme", never the bare word "theme" — SPEC.md §8's naming ban:
 * "The bare word 'theme' is banned in code and in copy." §3's own topbar
 * mockup labels this button "Theme", but §8 is the more specific, load-
 * bearing rule (M6.7/issue #34 adds an Interface theme control next, and a
 * bare "Theme" button is exactly the ambiguity §8 exists to prevent) — the
 * visible label here is "Diagram theme", not the mockup's shorthand.
 */
import { Check, ChevronDown, Palette } from 'lucide-react';
import { DIAGRAM_THEME_LABELS, DIAGRAM_THEMES, type DiagramTheme } from '../lib/mermaidRender';
import { useMenu } from './useMenu';

interface ThemeMenuProps {
  diagramTheme: DiagramTheme;
  onSelect: (diagramTheme: DiagramTheme) => void;
}

export default function ThemeMenu({ diagramTheme, onSelect }: ThemeMenuProps) {
  const { isOpen, containerRef, toggle, close } = useMenu<HTMLDivElement>();

  function handleSelect(next: DiagramTheme) {
    onSelect(next);
    close();
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
        className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3"
      >
        <Palette size={13} aria-hidden />
        Diagram theme
        <ChevronDown size={12} aria-hidden />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Diagram theme"
          className="absolute left-0 top-full z-10 mt-1 w-40 rounded-sm border border-ink-3 bg-ink-2 py-1 shadow-lg"
        >
          {DIAGRAM_THEMES.map((theme) => (
            <button
              key={theme}
              type="button"
              role="menuitemradio"
              aria-checked={theme === diagramTheme}
              onClick={() => handleSelect(theme)}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs text-text-ink hover:bg-ink-3"
            >
              {DIAGRAM_THEME_LABELS[theme]}
              {theme === diagramTheme && <Check size={12} aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
